import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import '../css/MSLPobOrganogram.css';
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import * as XLSX from "xlsx";
import CustomButton from "./CustomButton";

const MSLPobOrganogram = () => {
  const [states, setStates] = useState([{ id: "All", name: "All" }]);
  const [divisions, setDivisions] = useState([{ id: "All", name: "All" }]);
  const [employees, setEmployees] = useState([{ id: "All", first_name: "All", last_name: "" }]);
  const [selectedState, setSelectedState] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [retailerType, setRetailerType] = useState("All");
  const [activeStatus, setActiveStatus] = useState("All");
  const [startMonth, setStartMonth] = useState("");
  const [endMonth, setEndMonth] = useState("");
  const [financialYear, setFinancialYear] = useState("");
  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);

  const getFinancialYears = () => {
    const currentYear = new Date().getFullYear();
    let years = [];
    for (let y = 2024; y <= currentYear + 1; y++) {
      years.push(`${y}-${y + 1}`);
    }
    return years;
  };

  const fetchEmployees = useCallback(async (stateId, divisionName) => {
    try {
      let url = `${BASE_URL}/api/employees`;
      const params = {};
      
      if (stateId && stateId !== "All") {
        params.state_id = stateId;
      }
      
      if (divisionName && divisionName !== "All") {
        params.division = divisionName;
      }
      
      const res = await axios.get(url, { 
        params: Object.keys(params).length ? params : undefined,
        headers: getAuthHeaders() 
      });
      setEmployees([{ id: "All", first_name: "All", last_name: "" }, ...res.data]);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
    }
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() });
        setStates([{ id: "All", name: "All" }, ...response.data]);
      } catch (err) {
        console.error("Error fetching states:", err);
        setStates([{ id: "All", name: "All" }]);
      }
    };
    fetchStates();
  }, []);

  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");

    try {
      let url = `${BASE_URL}/api/divisions`;
      if (stateId && stateId !== "All") {
        url += `?state_id=${stateId}`;
      }
      
      const divisionRes = await axios.get(url, { headers: getAuthHeaders() });
      setDivisions([{ id: "All", name: "All" }, ...divisionRes.data]);
    } catch (err) {
      console.error("Error loading divisions", err);
      setDivisions([{ id: "All", name: "All" }]);
    }

    fetchEmployees(stateId, "All");
  };

  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    const division = divisions.find((d) => d.id == divisionId);
    const divisionName = division?.name || "All";
    fetchEmployees(selectedState, divisionName);
  };

  const handleGoClick = async () => {
    setShowTable(true);
    try {
      let url = `${BASE_URL}/api/combined-retailers`;
      const params = {};
      
      if (selectedEmployee && selectedEmployee !== "All") {
        params.employee_id = selectedEmployee;
      }
      
      if (selectedState && selectedState !== "All") {
        params.state_id = selectedState;
      }
      
      if (selectedDivision && selectedDivision !== "All") {
        const division = divisions.find((d) => d.id == selectedDivision);
        if (division && division.name) {
          params.division = division.name;
        }
      }
      
      if (retailerType !== "All") {
        params.retailer_type = retailerType.toLowerCase();
      }
      
      if (activeStatus !== "All") {
        params.active = activeStatus === "Yes";
      }
      
      if (startMonth) {
        params.start_date = `${startMonth}-01`;
      }
      if (endMonth) {
        params.end_date = `${endMonth}-01`;
      }
      
      const response = await axios.get(url, { 
        params: Object.keys(params).length ? params : undefined,
        headers: getAuthHeaders() 
      });
      setTableData(response.data);
      
    } catch (error) {
      console.error("Error fetching data:", error);
      setTableData([]);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate().toString().padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Visit/Support Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .header { text-align: center; margin-bottom: 20px; }
            .footer { text-align: center; margin-top: 20px; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>Visit/Support Report</h2>
          </div>
          ${document.querySelector(".mslpob_or-table-section").innerHTML}
          <div class="footer">
            Generated on: ${new Date().toLocaleString()}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleExportExcel = () => {
    if (tableData.length === 0) {
      alert("No data to export");
      return;
    }

    const worksheetData = [
      [
        "S.No", "Employee", "Type", "Code", "Retailer", "Active", "State", "City", 
        "Area", "Route", "MOBILE", "Address", "System Added Date", "Latest Visit Date",
        "Prev FY Avg", "Curr FY Avg", "Total Visits", "Total POB",
        ...['Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar'].flatMap(month => 
          [`${month} Visits`, `${month} POB`]
        )
      ],
      ...tableData.map((item, index) => [
        index + 1,
        item.employee_name || "N/A",
        item.type,
        item.code,
        item.name,
        item.active ? "Yes" : "No",
        item.state_name || "",
        item.city_name || "",
        item.area_name || "",
        item.route_name || "",
        item.mobile || "",
        item.address || "",
        formatDate(item.system_added_date),
        formatDate(item.latest_visit_date),
        item.fy_avg_prev || "",
        item.fy_avg_curr || "",
        item.total_visits || 0,
        item.total_pob || 0,
        ...['apr','may','jun','jul','aug','sep','oct','nov','dec','jan','feb','mar'].flatMap(month => 
          [item[`${month}_visit`] || 0, item[`${month}_pob`] || 0]
        )
      ])
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "VisitSupportReport");

    const fileName = `VisitSupportReport_${selectedState}_${selectedEmployee}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <div className="mslpob_or-main-container">
      <div className="mslpob_or-filter-container">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">MSLPOB</span> <span className="text-blue-500">Organogram</span>
        </h1>
        
        <div className="mslpob_or-row1">
          <label className="mslpob_or-label">State</label>
          <select 
            className="mslpob_or-dropdown" 
            onChange={(e) => handleStateChange(e.target.value)} 
            value={selectedState}
          >
            {states.map((state) => (
              <option key={state.id} value={state.id}>{state.name}</option>
            ))}
          </select>
          
          <label className="mslpob_or-label">Division</label>
          <select 
            className="mslpob_or-dropdown" 
            onChange={(e) => handleDivisionChange(e.target.value)} 
            value={selectedDivision}
          >
            {divisions.map((div) => (
              <option key={div.id} value={div.id}>{div.name}</option>
            ))}
          </select>
        </div>
        
        <div className="mslpob_or-row2">
          <label className="mslpob_or-label">Employee</label>
          <select 
            className="mslpob_or-dropdown" 
            onChange={(e) => setSelectedEmployee(e.target.value)} 
            value={selectedEmployee}
          >
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.id === "All" ? "All" : `${emp.first_name} ${emp.last_name} (${emp.designation} | ${emp.headquarter_name} | ${emp.division} | ${emp.state_name})`}
              </option>
            ))}
          </select>
        
          <label className="mslpob_or-label">Retailer Type</label>
          <select 
            className="mslpob_or-dropdown" 
            value={retailerType} 
            onChange={(e) => setRetailerType(e.target.value)}
          >
            <option value="All">All</option>
            <option value="Retailer">Retailer</option>
            <option value="Doctor">Doctor</option>
          </select>
        </div>

        <div className="mslpob_or-row2">
          <label className="mslpob_or-label">Financial Year</label>
          <select  
            className="mslpob_or-dropdown" 
            value={financialYear} 
            onChange={(e) => setFinancialYear(e.target.value)}
          >
            {getFinancialYears().map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>

          <label className="mslpob_or-label">Active</label>
          <select  
            className="mslpob_or-dropdown" 
            value={activeStatus} 
            onChange={(e) => setActiveStatus(e.target.value)}
          >
            <option value="All">All</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
      
        <div className="mslpob_or-go-button-container">
          <button className="mslpob_or-gobutton" onClick={handleGoClick}>GO</button>
        </div>
      </div>

      {showTable && (
        <>
          <div className="mslpob_or-table-section">
            <div className="mslpob_or-title3-container">
              <h2 className="mslpob_or-table-title">
                {selectedEmployee !== "All" 
                  ? `Employee: ${employees.find(e => e.id == selectedEmployee)?.first_name || ''} ${employees.find(e => e.id == selectedEmployee)?.last_name || ''}` 
                  : "All Employees"}
              </h2>
            </div>
            <div className="mslpob_or-table-wrapper">
              <table className="mslpob_or-table">
                <thead className="text-white sticky top-0 bg-gray-100">
                  <tr>
                    <th>S.No</th>
                    <th>Employee</th>
                    <th>Type</th>
                    <th>Code</th>
                    <th>Retailer</th>
                    <th>Active</th>
                    <th>State</th>
                    <th>City</th>
                    <th>Area</th>
                    <th>Route</th>
                    <th>MOBILE</th>
                    <th>Address</th>
                    <th>System Added Date</th>
                    <th>Latest Visit Date</th>
                    <th>Prev FY Avg</th>
                    <th>Curr FY Avg</th>
                    <th>Total Visits</th>
                    <th>Total POB</th>
                    {['Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar'].map(month => (
                      <React.Fragment key={month}>
                        <th>{month} Visits</th>
                        <th>{month} POB</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.employee_name || "N/A"}</td>
                      <td>{item.type}</td>
                      <td>{item.code}</td>
                      <td>{item.name}</td>
                      <td>{item.active ? "Yes" : "No"}</td>
                      <td>{item.state_name || ""}</td>
                      <td>{item.city_name || ""}</td>
                      <td>{item.area_name || ""}</td>
                      <td>{item.route_name || ""}</td>
                      <td>{item.mobile || ""}</td>
                      <td>{item.address || ""}</td>
                      <td>{formatDate(item.system_added_date)}</td>
                      <td>{formatDate(item.latest_visit_date)}</td>
                      <td>{item.fy_avg_prev || ""}</td>
                      <td>{item.fy_avg_curr || ""}</td>
                      <td>{item.total_visits || 0}</td>
                      <td>{item.total_pob || 0}</td>
                      {['apr','may','jun','jul','aug','sep','oct','nov','dec','jan','feb','mar'].map(month => (
                        <React.Fragment key={month}>
                          <td>{item[`${month}_visit`] || 0}</td>
                          <td>{item[`${month}_pob`] || 0}</td>
                        </React.Fragment>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="pob-datewise-export-buttons">
            <CustomButton
              onClick={handlePrint}
              label={"Print"}
              variant="print">
              🖨
            </CustomButton>

            <CustomButton
              onClick={handleExportExcel}
              label="Export to Excel"
              variant="export"
            > 
              📥
            </CustomButton>
          </div>
        </>
      )}
    </div>
  );
};

export default MSLPobOrganogram;